import { TASK_STATUS_LABELS, type Task } from '@stromo/shared';
import { diffDays, isTaskDelayed } from '../lib/delay';
import { formatDate } from '../lib/format';
import { useUsersLookup } from '../lib/lookups';

interface TaskCardListProps {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  highlightTaskId?: number | null;
  /** Sortier-Modus: kompakte Liste mit ↑/↓ statt Karten. Drag & Drop
   *  wie am Desktop funktioniert auf Touch-Geräten nicht zuverlässig. */
  sortMode?: boolean;
  /** Pflicht im Sortier-Modus — neue ID-Reihenfolge nach jedem Schritt. */
  onReorder?: (orderedIds: number[]) => void;
}

const STATUS_BG: Record<string, string> = {
  open: 'var(--status-open-bg)',
  planning: 'var(--status-planning-bg)',
  in_progress: 'var(--status-progress-bg)',
  delayed: 'var(--status-delayed-bg)',
  completed: 'var(--status-completed-bg)',
};
const STATUS_FG: Record<string, string> = {
  open: 'var(--status-open-text)',
  planning: 'var(--status-planning-text)',
  in_progress: 'var(--status-progress-text)',
  delayed: 'var(--status-delayed-text)',
  completed: 'var(--status-completed-text)',
};

export function TaskCardList({
  tasks,
  onTaskClick,
  highlightTaskId,
  sortMode,
  onReorder,
}: TaskCardListProps) {
  const users = useUsersLookup();

  if (tasks.length === 0) {
    return (
      <div className="empty-state" style={{ padding: 28 }}>
        <p>Keine Aufgaben in dieser Sektion.</p>
      </div>
    );
  }

  if (sortMode && onReorder) {
    return <TaskSortList tasks={tasks} onReorder={onReorder} />;
  }

  const getUserAbbr = (id: number | null) =>
    id ? users.data?.find((u) => u.id === id)?.abbreviation ?? null : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {tasks.map((t) => {
        if (t.is_group_header) {
          return (
            <div
              key={t.id}
              id={`task-row-${t.id}`}
              onClick={() => onTaskClick(t)}
              style={{
                background: 'var(--color-planning-bg)',
                color: 'var(--color-navy)',
                padding: '12px 14px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                marginTop: 10,
                cursor: 'pointer',
              }}
            >
              {t.title}
            </div>
          );
        }

        const delayed = isTaskDelayed(t);
        const delta = diffDays(t.schedule_end, t.internal_end);
        const smAbbr = getUserAbbr(t.site_manager_id);
        const isHighlighted = highlightTaskId === t.id;

        return (
          <div
            key={t.id}
            id={`task-row-${t.id}`}
            onClick={() => onTaskClick(t)}
            className={isHighlighted ? 'task-highlight' : ''}
            style={{
              background: '#fff',
              border: `1px solid ${delayed ? 'var(--color-danger-strong)' : 'var(--color-border)'}`,
              borderLeft: `4px solid ${
                delayed
                  ? 'var(--color-danger-strong)'
                  : STATUS_FG[t.status] ?? 'var(--color-border-strong)'
              }`,
              borderRadius: 10,
              padding: '12px 14px',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,.04)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
              <span
                style={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: 'var(--color-text-soft)',
                  fontSize: 13,
                  minWidth: 28,
                }}
              >
                {t.position_number}
              </span>
              <div style={{ flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--color-navy)', lineHeight: 1.3 }}>
                {t.title}
              </div>
              <span
                style={{
                  background: STATUS_BG[t.status] ?? 'var(--color-open-bg)',
                  color: STATUS_FG[t.status] ?? 'var(--color-open-text)',
                  padding: '4px 10px',
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                {TASK_STATUS_LABELS[t.status]}
              </span>
            </div>

            {/* Fortschritt */}
            {t.progress_percent > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div
                  style={{
                    height: 6,
                    background: 'var(--color-border-soft)',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${t.progress_percent}%`,
                      height: '100%',
                      background: STATUS_FG[t.status] ?? 'var(--color-primary)',
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--color-text-soft)',
                    marginTop: 3,
                    textAlign: 'right',
                  }}
                >
                  {t.progress_percent}%
                </div>
              </div>
            )}

            {/* Termine */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '6px 12px',
                fontSize: 12.5,
              }}
            >
              <DetailField label="Bauzeit von" value={formatDate(t.schedule_start)} />
              <DetailField label="bis" value={formatDate(t.schedule_end)} />
              {(t.internal_start || t.internal_end) && (
                <>
                  <DetailField label="Intern von" value={formatDate(t.internal_start)} />
                  <DetailField label="Intern bis" value={formatDate(t.internal_end)} />
                </>
              )}
              {delta !== null && (
                <DetailField
                  label="Differenz"
                  value={
                    <span
                      style={{
                        color: delta > 0
                          ? 'var(--color-danger-text)'
                          : delta < 0
                          ? 'var(--color-success-text)'
                          : 'var(--color-text-strong)',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                      }}
                    >
                      {delta > 0 ? `+${delta}` : delta} Tage
                    </span>
                  }
                />
              )}
              {smAbbr && <DetailField label="Bauleiter" value={smAbbr} />}
              {t.subcontractor && (
                <DetailField label="Subunternehmer" value={t.subcontractor} />
              )}
            </div>

            {t.notes && (
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 8,
                  borderTop: '1px solid var(--color-divider)',
                  fontSize: 12.5,
                  color: 'var(--color-text-strong)',
                }}
              >
                <span style={{ color: 'var(--color-text-soft)', fontWeight: 600 }}>
                  Notiz:{' '}
                </span>
                {t.notes}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Kompakte Sortier-Ansicht: pro Zeile Nummer, Titel und ↑/↓. Jeder
 *  Tipp tauscht mit dem Nachbarn und speichert sofort (optimistisch). */
function TaskSortList({
  tasks,
  onReorder,
}: {
  tasks: Task[];
  onReorder: (orderedIds: number[]) => void;
}) {
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= tasks.length) return;
    const ids = tasks.map((t) => t.id);
    const [moved] = ids.splice(idx, 1);
    if (moved === undefined) return;
    ids.splice(target, 0, moved);
    onReorder(ids);
  };

  return (
    <div className="task-sort-list">
      {tasks.map((t, idx) => (
        <div
          key={t.id}
          id={`task-row-${t.id}`}
          className={`task-sort-row${t.is_group_header ? ' is-group' : ''}`}
        >
          {!t.is_group_header && <span className="task-sort-row__num">{t.position_number}</span>}
          <span className="task-sort-row__title">{t.title}</span>
          <button
            type="button"
            className="task-sort-row__btn"
            onClick={() => move(idx, -1)}
            disabled={idx === 0}
            aria-label={`„${t.title}" nach oben`}
          >
            ↑
          </button>
          <button
            type="button"
            className="task-sort-row__btn"
            onClick={() => move(idx, 1)}
            disabled={idx === tasks.length - 1}
            aria-label={`„${t.title}" nach unten`}
          >
            ↓
          </button>
        </div>
      ))}
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span
        style={{
          fontSize: 10.5,
          color: 'var(--color-text-soft)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <span style={{ color: 'var(--color-text-strong)' }}>{value}</span>
    </div>
  );
}
